<script setup>
import { computed, ref, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['sendMessage', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const headerMediaUrl = ref('');

const hasHeaderMedia = computed(() => {
  const headerComponent = props.template?.components?.find(
    comp => comp.type === 'HEADER'
  );
  return (
    headerComponent &&
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)
  );
});

const headerMediaType = computed(() => {
  if (!hasHeaderMedia.value) return null;
  const headerComponent = props.template?.components?.find(
    comp => comp.type === 'HEADER'
  );
  return headerComponent?.format;
});

const templateName = computed(() => {
  return props.template?.name || '';
});

const templateString = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'BODY'
  ).text;
});

const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const processedString = computed(() => {
  return templateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.value[variableKey] || `{{${variable}}}`;
  });
});

const processedStringWithVariableHighlight = computed(() => {
  const variables = templateString.value.match(/{{([^}]+)}}/g) || [];

  return variables.reduce((result, variable) => {
    const variableKey = processVariable(variable);
    const value = processedParams.value[variableKey] || variable;
    return result.replace(
      variable,
      `<span class="break-all text-n-slate-12">${value}</span>`
    );
  }, templateString.value);
});

const rules = computed(() => {
  const paramRules = {};
  Object.keys(processedParams.value).forEach(key => {
    paramRules[key] = { required: requiredIf(true) };
  });
  return {
    processedParams: paramRules,
    headerMediaUrl: {
      required: requiredIf(hasHeaderMedia.value),
    },
  };
});

const v$ = useVuelidate(rules, { processedParams, headerMediaUrl });

const getFieldErrorType = key => {
  if (!v$.value.processedParams[key]?.$error) return 'info';
  return 'error';
};

const generateVariables = () => {
  const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
  if (!matchedVariables) return;

  const finalVars = matchedVariables.map(i => processVariable(i));
  processedParams.value = finalVars.reduce((acc, variable) => {
    acc[variable] = '';
    return acc;
  }, {});
};

const sendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  let messageContent = processedString.value;

  // Add multimedia indicator if template has multimedia
  if (hasHeaderMedia.value) {
    const indicator = getMultimediaIndicator(headerMediaType.value);
    messageContent = `${indicator}\n\n${messageContent}`;
  }

  const payload = {
    message: messageContent,
    templateParams: {
      name: props.template.name,
      category: props.template.category,
      language: props.template.language,
      namespace: props.template.namespace,
      processed_params: processedParams.value,
    },
  };

  // Add header_handle if template has multimedia and URL is provided
  if (hasHeaderMedia.value && headerMediaUrl.value) {
    payload.templateParams.header_handle = headerMediaUrl.value;
  }

  emit('sendMessage', payload);
};

const getMultimediaIndicator = (type) => {
  const indicators = {
    IMAGE: '[📷 Imagen]',
    VIDEO: '[🎥 Video]',
    DOCUMENT: '[📄 Documento]',
  };
  return indicators[type] || '[📎 Archivo multimedia]';
};

onMounted(() => {
  generateVariables();
});
</script>

<template>
  <div
    class="absolute top-full mt-1.5 max-h-[30rem] overflow-y-auto left-0 flex flex-col gap-4 px-4 pt-6 pb-5 items-start w-[28.75rem] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
  >
    <span class="text-sm text-n-slate-12">
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.TEMPLATE_NAME',
          { templateName: templateName }
        )
      }}
    </span>
    <p
      class="mb-0 text-sm text-n-slate-11"
      v-html="processedStringWithVariableHighlight"
    />

    <!-- Header media URL input if template has multimedia -->
    <div
      v-if="hasHeaderMedia"
      class="w-full p-3 border rounded-md bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
    >
      <p class="mb-2 text-xs font-medium text-blue-900 dark:text-blue-100">
        {{ headerMediaType }}
        {{
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.URL_REQUIRED'
          )
        }}
      </p>
      <Input
        v-model="headerMediaUrl"
        type="url"
        :placeholder="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.URL_PLACEHOLDER',
            { type: headerMediaType?.toLowerCase() }
          )
        "
        custom-input-class="!h-8 w-full !bg-white dark:!bg-n-slate-1"
        class="w-full"
        :message-type="v$.headerMediaUrl.$error ? 'error' : 'info'"
      />
      <p class="mt-1 text-xs text-blue-600 dark:text-blue-400">
        {{
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.URL_HELP',
            { type: headerMediaType?.toLowerCase() }
          )
        }}
      </p>
    </div>

    <span
      v-if="Object.keys(processedParams).length"
      class="text-sm font-medium text-n-slate-12"
    >
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.VARIABLES'
        )
      }}
    </span>

    <div
      v-for="(variable, key) in processedParams"
      :key="key"
      class="flex items-center w-full gap-2"
    >
      <span
        class="flex items-center h-8 text-sm min-w-6 ltr:text-left rtl:text-right text-n-slate-10"
      >
        {{ key }}
      </span>
      <Input
        v-model="processedParams[key]"
        custom-input-class="!h-8 w-full !bg-transparent"
        class="w-full"
        :message-type="getFieldErrorType(key)"
      />
    </div>

    <div class="flex items-end justify-between w-full gap-3 h-14">
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.BACK'
          )
        "
        color="slate"
        variant="faded"
        class="w-full font-medium"
        @click="emit('back')"
      />
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.SEND_MESSAGE'
          )
        "
        class="w-full font-medium"
        @click="sendMessage"
      />
    </div>
  </div>
</template>
